import { ObjectTools } from '../../../tools/object-tools';
import { SolIcon } from '@sol/icons';
import { SolNavbarItem, SolRouteId, SolRouteIds } from '../../../models/sol-navigation';
import { SolMenuGroup } from '../../popover/sol-popover-menu-content/sol-popover-menu-content.state';
import { MenuTools } from '../../../tools/menu-tools';

export class SolNavbarItemState {
  private constructor(
    public readonly item: SolNavbarItem,
    public readonly activeRouteIds: SolRouteId[],
    public readonly expandedIds: SolRouteId[],
    public readonly level: number,
    public readonly text: string,
    public readonly prependIcon: SolIcon | undefined,
    public readonly appendIcon: SolIcon | undefined,
    public readonly selected: boolean,
    public readonly expanded: boolean,
    public readonly isTopLevelGroup: boolean,
    public readonly isMenuGroup: boolean,
    public readonly children: SolNavbarItem[],
    public readonly buttonClasses: string,
    public readonly hoverPreviewEnabled: boolean,
    public readonly hoverPreviewMenu: SolMenuGroup[],
  ) {}

  public static of(): SolNavbarItemState {
    return new SolNavbarItemState(
      SolNavbarItem.of(SolRouteIds.error),
      [],
      [],
      0,
      '',
      undefined,
      SolIcon.chevron_down,
      false,
      false,
      false,
      false,
      [],
      '',
      false,
      [],
    ).recalculate();
  }

  public withItem(item: SolNavbarItem): SolNavbarItemState {
    return this.copy({ item }).recalculate();
  }

  public withActiveRouteIds(activeRouteIds: SolRouteId[]): SolNavbarItemState {
    return this.copy({ activeRouteIds }).recalculate();
  }

  public withExpandedIds(expandedIds: SolRouteId[]): SolNavbarItemState {
    return this.copy({ expandedIds }).recalculate();
  }

  public withLevel(level: number): SolNavbarItemState {
    return this.copy({ level }).recalculate();
  }

  private recalculate(): SolNavbarItemState {
    const item = this.item;
    const isTopLevelGroup = item.isMenuGroup && this.level === 0;
    const text = item.displayName || '';
    const selected = this.activeRouteIds.includes(item.id);
    const expanded = this.expandedIds.includes(item.id);
    const children = item.children.filter(child => child.hasAuthority);
    const prependIcon = item.icon;
    const isMenuGroup = item.isMenuGroup;
    const hoverPreviewEnabled = isMenuGroup && !isTopLevelGroup && !expanded && children.length > 0;
    const appendIcon = this.toAppendIcon(isMenuGroup, expanded, hoverPreviewEnabled);
    const hoverPreviewMenu = hoverPreviewEnabled ? MenuTools.navbarToMenu(children) : [];
    return this.copy({
      isMenuGroup,
      isTopLevelGroup,
      text,
      selected,
      children,
      prependIcon,
      appendIcon,
      expanded,
      hoverPreviewEnabled,
      hoverPreviewMenu,
    }).recalculateClasses();
  }

  private toAppendIcon(isMenuGroup: boolean, expanded: boolean, hoverPreviewEnabled: boolean): SolIcon {
    if (!isMenuGroup) {
      return SolIcon.chevron_down;
    }
    if (expanded) {
      return SolIcon.chevron_up;
    }
    return hoverPreviewEnabled ? SolIcon.chevron_right : SolIcon.chevron_down;
  }

  private recalculateClasses(): SolNavbarItemState {
    let buttonClasses: string;
    if (this.level < 2) {
      if (this.item.isMenuGroup) {
        buttonClasses = 'sol-navbar-menu-toggle-button';
      } else {
        buttonClasses = 'sol-navbar-menu-button';
      }
    } else {
      buttonClasses = 'sol-navbar-menu-group-button';
    }

    if (this.selected && !this.item.isMenuGroup) {
      buttonClasses += ' sol-navbar-menu-button-selected';
    }
    return this.copy({ buttonClasses });
  }

  private copy(props: Partial<SolNavbarItemState>): SolNavbarItemState {
    return ObjectTools.copy<SolNavbarItemState>(this, props);
  }
}
